import {
  Box,
  FormControlLabel,
  Paper,
  Radio,
  RadioGroup,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from '@material-ui/core';
import { Canceler } from 'axios';
import queryString from 'query-string';
import React, { Fragment, useCallback, useEffect, useReducer, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import useConfirmDialog from '../../../hooks/useConfirmDialog';
import useSnackbar from '../../../hooks/useSnackbar';
import api from '../../../services/api/base';
import {
  banCommentAndUser,
  getComments,
  saveCommentReviewInternalNote,
  saveCommentReviewStatus,
  unbanCommentAndUser,
} from '../../../services/api/Moderation';
import {
  CommentReviewId,
  CommentReviewStatus,
  IComment,
  ICommentRequestParamDto,
  ICommentReview,
  ISuccessResult,
  ModerationSearchType,
  ModerationType,
  ReviewSortOrder,
} from '../../../services/types';
import InternalNote from '../Comment/CommentInternalNote';
import DateRangePicker from '../../../components/DateRangePicker';
import TablePaginationActions from '../common/PaginationAction';
import Search from '../common/Search';
import reducer, { CommentActionTypes } from './Comment.reducer';
import useStyles from './Comment.styles';
import ReportViewer from './CommentReportViewer';
import CommentTableRow from './CommentTableRow';

interface IProps {
  isBannedUserMode: boolean;
  isShownReportedOnly: boolean;
}

const RowsPerPage = 50;

const Comment = (props: IProps): JSX.Element => {
  const classes = useStyles();

  const history = useHistory();
  const location = useLocation();

  const { commentId, startDate, endDate, userId, storyId, seasonId, displayName, reviewStatus } = queryString.parse(location.search);

  const { isBannedUserMode, isShownReportedOnly } = props;

  const initialState: ICommentRequestParamDto = {
    startDate: startDate ? new Date(String(startDate)).toISOString() : undefined,
    endDate: endDate ? new Date(String(endDate)).toISOString() : undefined,
    reviewStatus: (reviewStatus as CommentReviewStatus) || CommentReviewStatus.NotReviewed,
    userId: userId ? Number(userId) : undefined,
    storyId: storyId ? Number(storyId) : undefined,
    seasonId: seasonId ? Number(seasonId) : undefined,
    commentId: commentId ? Number(commentId) : undefined,
    displayName: displayName ? String(displayName) : undefined,
    order: isBannedUserMode ? ReviewSortOrder.Newest : ReviewSortOrder.Oldest,
  };

  const [isListLoading, setIsListLoading] = useState<boolean>(false);
  const [comments, setComments] = useState<IComment[]>([]);
  const [page, setPage] = useState<number>(0);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [showReportViewer, setShowReportViewer] = useState<boolean>(false);
  const [showInternalNote, setShowInternalNote] = useState<boolean>(false);
  const [currentViewComment, setCurrentViewComment] = useState<IComment>();
  const [currentSelectedCommentIds, setCurrentSelectedCommentIds] = useState<Set<number>>(new Set<number>());
  const [isPendingChangeReviewStatus, setIsPendingChangeReviewStatus] = useState<boolean>(false);

  const [state, dispatch] = useReducer(reducer, initialState);

  const snackbar = useSnackbar();
  const confirm = useConfirmDialog();

  const commentsCanceler = useRef<Canceler>(() => undefined);

  const handleDateRangeSelect = useCallback((startDate: Date | null, endDate: Date | null) => {
    dispatch({ type: CommentActionTypes.SetDate, payload: { startDate: startDate?.toISOString(), endDate: endDate?.toISOString() } });
    setPage(0);
  }, []);

  const handleReviewStatusFilterChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const reviewStatus = e.target.value as CommentReviewStatus;
    dispatch({ type: CommentActionTypes.SetReviewStatus, payload: { reviewStatus } });

    setPage(0);
  }, []);

  const handleSearchChange = useCallback(
    (searchType: ModerationSearchType, query: string) => {
      if (searchType === ModerationSearchType.DisplayName) {
        dispatch({ type: CommentActionTypes.SetDisplayName, payload: { displayName: query } });
        history.push(`${location.pathname}?displayName=${query}`);
      }

      if (searchType === ModerationSearchType.UserId) {
        dispatch({ type: CommentActionTypes.SetUserId, payload: { userId: query ? Number(query) : undefined } });
        history.push(`${location.pathname}?userId=${query}`);
      }

      if (searchType === ModerationSearchType.StoryId) {
        dispatch({ type: CommentActionTypes.SetStoryId, payload: { storyId: query ? Number(query) : undefined } });
        history.push(`${location.pathname}?storyId=${query}`);
      }

      if (searchType === ModerationSearchType.SeasonId) {
        dispatch({ type: CommentActionTypes.SetSeasonId, payload: { seasonId: query ? Number(query) : undefined } });
        history.push(`${location.pathname}?seasonId=${query}`);
      }

      if (searchType === ModerationSearchType.CommentId) {
        dispatch({ type: CommentActionTypes.SetCommentId, payload: { commentId: query ? Number(query) : undefined } });
        history.push(`${location.pathname}?commentId=${query}`);
      }

      if (!query) {
        history.push(location.pathname);
      }

      setPage(0);
    },
    [history, location],
  );

  const getSearchTypeAndQuery = (): [ModerationSearchType, any] => {
    if (state.storyId) {
      return [ModerationSearchType.StoryId, state.storyId];
    }

    if (state.seasonId) {
      return [ModerationSearchType.SeasonId, state.seasonId];
    }

    if (state.commentId) {
      return [ModerationSearchType.CommentId, state.commentId];
    }

    if (state.displayName) {
      return [ModerationSearchType.DisplayName, state.displayName];
    }

    return [ModerationSearchType.UserId, state.userId];
  };

  const [currentSearchType, currentSearchQuery] = getSearchTypeAndQuery();

  const fetchComments = useCallback(
    async (page: number, rowsPerPage: number, param: ICommentRequestParamDto) => {
      const p = { ...param, isReported: isShownReportedOnly || undefined };

      if (isBannedUserMode) {
        p.reviewStatus = CommentReviewStatus.Banned;
      }

      if (!p.userId) {
        p.userId = undefined;
      }

      if (!p.storyId) {
        p.storyId = undefined;
      }

      if (!p.seasonId) {
        p.seasonId = undefined;
      }

      if (!p.commentId) {
        p.commentId = undefined;
      }

      if (!p.reviewStatus || p.reviewStatus === CommentReviewStatus.All) {
        p.reviewStatus = undefined;
      }

      if (!p.displayName) {
        p.displayName = undefined;
      }

      setIsListLoading(true);

      commentsCanceler.current();

      try {
        const [fetchPromise, cancel] = getComments(p, page + 1, rowsPerPage);
        commentsCanceler.current = cancel;
        const result = await fetchPromise;

        setComments(result.data.items);
        setTotalCount(result.data?.totalCount || 0);
      } catch (e) {
        if (api.isCancel(e)) return;
      } finally {
        setIsListLoading(false);
        setCurrentSelectedCommentIds(new Set<number>());
      }
    },
    [isShownReportedOnly, isBannedUserMode],
  );

  const handlePageChange = useCallback((e: unknown, page: number) => {
    setPage(page);
  }, []);

  const saveBannedCommentAndUser = useCallback(
    async (commentReviewId: CommentReviewId, isBanned: boolean, onSuccess?: () => void) => {
      setIsPendingChangeReviewStatus(true);
      try {
        let result;

        if (isBanned) {
          result = await banCommentAndUser(commentReviewId);
        } else {
          result = await unbanCommentAndUser(commentReviewId);
        }

        const { data: affectedReview } = result.data as ISuccessResult<ICommentReview>;

        if (api.isResponseSuccess(result)) {
          const cloned = [...comments];
          const comment = cloned.find(c => c.id === affectedReview.commentId);

          if (comment) {
            cloned
              .filter(c => c.userId === comment.userId)
              .forEach(c => {
                c.isBannedUser = isBanned;
              });
            comment.review = affectedReview;
            comment.isBannedUser = isBanned;
          }

          setComments(cloned);

          if (onSuccess) {
            onSuccess();
          }
        } else {
          snackbar.error(`The status cannot be saved.`);
        }
      } catch (e) {
        snackbar.error(`The status cannot be saved. ${e.message}`);
      } finally {
        setIsPendingChangeReviewStatus(false);
      }
    },
    [comments, snackbar],
  );

  const saveReviewStatus = useCallback(
    async (commentReviews: ICommentReview[], reviewStatus: CommentReviewStatus, onSuccess?: () => void) => {
      setIsPendingChangeReviewStatus(true);
      try {
        const result = await saveCommentReviewStatus(
          commentReviews.map(r => r.id),
          reviewStatus,
        );

        const apiResult = result.data as ISuccessResult<ICommentReview[]>;
        const { data: reviews } = apiResult;

        if (api.isResponseSuccess(result)) {
          const cloned = [...comments];

          reviews.forEach(r => {
            const comment = cloned.find(c => c.id === r.commentId);
            if (comment) {
              comment.review = r;
            }
          });

          setComments(cloned);
          if (onSuccess) {
            onSuccess();
          }
        } else {
          snackbar.error('The status cannot be saved.');
        }
      } catch (e) {
        snackbar.error(`The status cannot be saved. ${e.message}`);
      } finally {
        setIsPendingChangeReviewStatus(false);
      }
    },
    [comments, snackbar],
  );

  const handleReviewStatusOfEachItemChange = useCallback(
    (commentReview: ICommentReview, reviewStatus: CommentReviewStatus) => {
      const banUser = (commentReviewId: CommentReviewId) => () => saveBannedCommentAndUser(commentReviewId, true);
      const unbanUser = (commentReviewId: CommentReviewId) => () => saveBannedCommentAndUser(commentReviewId, false);
      const saveReview = () => saveReviewStatus([commentReview], reviewStatus);

      const findComment = (commentId: number) => {
        const comment = comments.find(c => c.id === commentId);
        if (!comment || !comment.userId) {
          return;
        }

        return comment;
      };

      if (reviewStatus === CommentReviewStatus.HiddenByBan || reviewStatus === CommentReviewStatus.Banned) {
        const comment = findComment(commentReview.commentId);
        if (!comment) {
          return;
        }

        confirm.open({
          title: `Ban this user?`,
          text: `If you ban this user, his/her all previous comments will be hidden and no longer able to participate in the Radish Community (chat rooms, comments) any longer. Are you sure you want to ban [${comment.displayName}]?`,
          proceedButtonText: 'Yes',
          cancelButtonText: 'No',
          onProceed: banUser(commentReview.id),
        });

        return;
      } else if (reviewStatus === CommentReviewStatus.Unban) {
        const comment = findComment(commentReview.commentId);
        if (!comment) {
          return;
        }

        confirm.open({
          title: `Unban this user?`,
          text: `Are you sure you want to unban this user?`,
          proceedButtonText: 'I am sure.',
          cancelButtonText: 'No',
          onProceed: unbanUser(commentReview.id),
        });

        return;
      } else if (reviewStatus === CommentReviewStatus.Hidden) {
        confirm.open({
          title: `Hide this comment?`,
          text: `Are you sure you want to hide this comment?`,
          proceedButtonText: 'I am sure.',
          cancelButtonText: 'No',
          onProceed: saveReview,
        });

        return;
      } else if (reviewStatus === CommentReviewStatus.Approved) {
        const comment = findComment(commentReview.commentId);
        if (!comment) {
          return;
        }

        if (comment.review.status === CommentReviewStatus.Hidden) {
          confirm.open({
            title: `Approve this comment?`,
            text: 'This comment has been hidden. Are you sure you want to change its status to "Approved"?',
            proceedButtonText: 'I am sure.',
            cancelButtonText: 'No',
            onProceed: saveReview,
          });
          return;
        }
      }

      saveReview();
    },
    [saveReviewStatus, saveBannedCommentAndUser, comments, confirm],
  );

  const handleReportClick = useCallback((comment: IComment) => {
    setCurrentViewComment(comment);
    setShowReportViewer(true);
    setShowInternalNote(false);
  }, []);

  const handleInternalNoteClick = useCallback((comment: IComment) => {
    setCurrentViewComment(comment);
    setShowInternalNote(true);
    setShowReportViewer(false);
  }, []);

  const handleInternalNoteSave = useCallback((commentId: number, note: string) => {
    setComments(ps => {
      const cloned = [...ps];

      const comment = cloned.find(c => c.id === commentId);
      if (comment && comment.review) {
        comment.review.note = note;
      }
      return cloned;
    });

    setShowInternalNote(false);
  }, []);

  const handleInternalNoteClose = useCallback(() => {
    setShowInternalNote(false);
  }, []);

  const handleReportViewerClose = useCallback(() => {
    setShowReportViewer(false);
  }, []);

  const handleTableRowCheckChange = useCallback((comment: IComment) => {
    const { id } = comment;

    setCurrentSelectedCommentIds(ps => {
      const cloned = new Set(ps);

      if (cloned.has(id)) {
        cloned.delete(id);
      } else {
        cloned.add(id);
      }

      return cloned;
    });
  }, []);

  const handleSortOptionChange = useCallback((order: ReviewSortOrder) => {
    dispatch({ type: CommentActionTypes.SetOrder, payload: { order } });
  }, []);

  const handleApproveButtonClick = useCallback(() => {
    saveReviewStatus(
      comments.filter(c => currentSelectedCommentIds.has(c.id)).map(c => c.review),
      CommentReviewStatus.Approved,
      () => setCurrentSelectedCommentIds(new Set<number>()),
    );
  }, [comments, currentSelectedCommentIds, saveReviewStatus]);

  const handleSelectAllCheckboxClick = useCallback(() => {
    setCurrentSelectedCommentIds(ps => {
      const cloned = new Set(ps);

      const selectableComments = comments.filter(c => !c.isBannedUser);

      if (cloned.size >= selectableComments.length) {
        cloned.clear();
      } else {
        selectableComments.forEach(c => cloned.add(c.id));
      }

      return cloned;
    });
  }, [comments]);

  const ActionsComponent = useCallback(
    TablePaginationActions({
      order: state.order || ReviewSortOrder.Newest,
      disabledButtons: isPendingChangeReviewStatus,
      selectedItemsCount: currentSelectedCommentIds.size,
      itemsTotalCount: comments.filter(c => !c.isBannedUser).length,
      disableReportedSortOption: isShownReportedOnly,
      onOrderChange: handleSortOptionChange,
      onApproveButtonClick: !isBannedUserMode ? handleApproveButtonClick : undefined,
      onSelectAllCheckboxClick: !isBannedUserMode ? handleSelectAllCheckboxClick : undefined,
    }),
    [
      isPendingChangeReviewStatus,
      currentSelectedCommentIds,
      comments,
      handleSortOptionChange,
      handleApproveButtonClick,
      handleSelectAllCheckboxClick,
    ],
  );

  useEffect(() => {
    const { startDate, endDate } = state;

    if (!isBannedUserMode && (!startDate || !endDate)) {
      return;
    }

    fetchComments(page, RowsPerPage, state);

    return () => commentsCanceler.current();
  }, [fetchComments, page, state, isBannedUserMode]);

  const renderTablePagination = () => (
    <TablePagination
      ActionsComponent={ActionsComponent}
      count={totalCount}
      page={page}
      rowsPerPage={RowsPerPage}
      rowsPerPageOptions={[]}
      onChangePage={handlePageChange}
      labelDisplayedRows={() => null}
      classes={{
        toolbar: classes.paginationToolbar,
      }}
    />
  );

  return (
    <Box display="flex" flexDirection="column" paddingTop={'6px'}>
      {isBannedUserMode && (
        <Box className={classes.bannedUserTitleWrap}>
          <Typography variant="h6">Banned Users</Typography>
          <Box className={classes.bannedUserDescription}>
            Once user is banned, all comments of this user will be hidden immediately.
            <br /> Also, This user can no longer write new comments on the entire story and enter radish live.
          </Box>
        </Box>
      )}
      {!isBannedUserMode && (
        <Fragment>
          <DateRangePicker startDate={state.startDate} endDate={state.endDate} onChange={handleDateRangeSelect} />
          <RadioGroup row value={state.reviewStatus} onChange={handleReviewStatusFilterChange} className={classes.radioButtonGroups}>
            <FormControlLabel value={CommentReviewStatus.All} control={<Radio color="primary" />} label="All Status" />
            <FormControlLabel value={CommentReviewStatus.NotReviewed} control={<Radio color="primary" />} label="Not reviewed" />
            <FormControlLabel value={CommentReviewStatus.Approved} control={<Radio color="primary" />} label="Approved" />
            <FormControlLabel value={CommentReviewStatus.Pending} control={<Radio color="primary" />} label="Pending" />
            <FormControlLabel value={CommentReviewStatus.Hidden} control={<Radio color="primary" />} label="Hidden" />
            <FormControlLabel value={CommentReviewStatus.HiddenByBan} control={<Radio color="primary" />} label="Hidden by ban" />
          </RadioGroup>
        </Fragment>
      )}

      <Search query={currentSearchQuery} type={currentSearchType} moderationType={ModerationType.Comments} onChange={handleSearchChange} />
      <Paper className={classes.tablePaperWrap}>
        {isListLoading && <Box className={classes.loadingWrap} />}
        {renderTablePagination()}
        <Table>
          <TableHead>
            <TableRow>
              {!isBannedUserMode && <TableCell padding="checkbox" />}
              <TableCell padding={!isBannedUserMode ? 'checkbox' : 'default'} className={classes.tableCell80}>
                {isShownReportedOnly ? 'Reported At' : 'Created At'}
              </TableCell>
              <TableCell padding="checkbox" className={classes.tableCell80}>
                User ID
              </TableCell>
              <TableCell>Story ID / Season ID / Episode # / Parent ID / Comments</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Reported</TableCell>
              <TableCell>Internal Note</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {comments.length > 0 &&
              comments.map(c => (
                <CommentTableRow
                  isBannedUserMode={isBannedUserMode}
                  isReported={isShownReportedOnly}
                  key={c.id}
                  comment={c}
                  disableReviewStatusChange={isPendingChangeReviewStatus}
                  isChecked={currentSelectedCommentIds.has(c.id)}
                  onCheckboxChange={handleTableRowCheckChange}
                  onReviewStatusChange={handleReviewStatusOfEachItemChange}
                  onReportClick={handleReportClick}
                  onInternalNoteClick={handleInternalNoteClick}
                />
              ))}
          </TableBody>
        </Table>
        {comments.length === 0 && (
          <Box className={classes.noResult}>
            <span>No result</span>
          </Box>
        )}
        {renderTablePagination()}
      </Paper>

      {showReportViewer && <ReportViewer comment={currentViewComment} onClose={handleReportViewerClose} />}
      {showInternalNote && (
        <InternalNote
          saveFactory={saveCommentReviewInternalNote}
          comment={currentViewComment}
          onSave={handleInternalNoteSave}
          onClose={handleInternalNoteClose}
        />
      )}
    </Box>
  );
};

export default Comment;
